"use client";

import { createRef, RefObject, useState, useEffect, useContext, useMemo, FC } from "react";

import classNames from "classnames";

import omit from "lodash/omit";
import mapValues from "lodash/mapValues";
import isEmpty from "lodash/isEmpty";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { LOGIN_CALLBACK_ROUTE, useAuth0V2 } from "@stadium/services/auth0";
import { gtagEvent } from "@stadium/services/google-analytics";
import {
  initialize as initializeChiliPiper,
  submit as submitChiliPiper,
  getRouter as getChiliPiperRouter,
} from "@stadium/services/chili-piper";
import { parseParamsForHubspot, submitFormData } from "@stadium/services/hubspot";
import { useGoogleReCaptcha } from "@stadium/services/google-recaptcha";

import { bookACallApi, postHubspotFormData } from "apis/book-a-call";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { useCountryMapping } from "@stadium/shared/src/hooks/use-country-mapping";

import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";

import SelectDropdown from "@stadium/ui/src/components/form/SelectDropdown";
import TextInput from "@stadium/ui/src/components/form/TextInput";
import TextAreaInput from "@stadium/ui/src/components/form/TextAreaInput";
import CheckboxInput from "@stadium/ui/src/components/form/CheckboxInput";
import Label from "@stadium/ui/src/components/form/Label";
import CountryFlag from "@stadium/ui/src/shared/country-flag";
import Button from "@stadium/shared/src/components/shared/button";
import { PageLoader, Spinner } from "@stadium/ui/src/components/loaders/loaders";
import ReactWindowList from "@stadium/ui/src/components/react-window-list";
import SingleSelectDropdown from "@stadium/shared/src/components/book-a-call/single-select-dropdown";
import CustomizeDemoDropdown from "@stadium/shared/src/components/book-a-call/customize-demo-dropdown";

import { useJuneSoComponentContext } from "@stadium/services/june-analytics";
import { useBookACallText } from "@stadium/shared/src/hooks/book-a-call";

import { getJsonCookie } from "@stadium/helpers/cookie";
import { Validator } from "@stadium/helpers/form/validation";
import { isNotWorkEmail, getFirstAndLastName } from "@stadium/helpers/form/utils";
import { useFlags } from "@stadium/services/launch-darkly";
import { withSyncFeatureFlag } from "@stadium/services/launch-darkly/utils";
import {
  scrollToContainerWithRef,
  scrollInContainer as scrollToTop,
} from "@stadium/helpers/animate";
import {
  getDisplayedSourceOrigin,
  getFormattedOfferings,
  getFormattedResponse,
  getFormattedResponseForAnalytics,
  getFormattedResponseForAuthParams,
} from "helpers/book-a-call";
import { isAutomationEmail, isGmailAccount } from "@stadium/helpers/emails";
import { normalizePhone } from "@stadium/helpers/form/normalizer";

import { APP_NAME } from "constants/app-defaults";
import {
  BASE_PATH,
  GOOGLE_RECAPTCHA_SITEKEY,
  HUBSPOT_BOOK_A_CALL_FORM_GUID,
} from "constants/env-variables";
import { DEFAULT_COUNTRY_ISO, SERVER_PHASE } from "@stadium/constants";
import { GDPR_COUNTRIES_ISO } from "@stadium/constants/country";
import { SWAGMAGIC } from "@stadium/services/chili-piper/constant";
import { SWAGMAGIC_URL } from "@stadium/helpers/src/app-routes";
import {
  DEFAULT_FORM_VALUES,
  DEFAULT_OBJ,
  EMAIL_WARNING,
  STADIUM_BOOK_A_CALL_TREAT_TYPE,
  BOOK_A_CALL_FORM_SUBMITTED,
  CALL_BOOKED,
  BOOK_A_CALL_HUBSPOT_FORM_MAPPING,
  BOOK_A_CALL_PAGE_NAME,
  STADIUM_USAGE,
  TEAM_INPUT,
  OTHER_INPUT_FIELDS_KEY,
  FORM_FIELDS_KEYS,
  COMPANY_INPUT,
} from "constants/book-a-call";
import { THANK_YOU_PAGE_URL } from "constants/routes";
import { BOOK_A_CALL, BOOK_A_CALL_SERVICES } from "constants/june-so";
import { hubspotContextRefactorFlag } from "@stadium/services/launch-darkly/constants";

import defaultStyles from "./main.module.scss";

type BookACallFormProps = {
  title?: string;
  showTitle?: boolean;
  styles?: ModularStyles;
  containerRef?: RefObject<HTMLDivElement>;
  onBookACallSubmit?: () => Promise<{
    cartInfo: string;
    shopNumber: string;
  }>;
};

export type OtherInputProps = {
  title?: string;
  subLabel?: string;
  fieldName: string;
  otherPlaceHolderText?: string;
  onToggle?: () => void;
  renderTextArea?: boolean;
};

const getPageName = () => (SERVER_PHASE ? "" : document.title.trim() || BOOK_A_CALL_PAGE_NAME);

const emptyObj: DefaultObjType = {};
// just removing the msg prop in render because may need to show error msg soon, so not cleaning up this helper component
const ErrorMsg = ({ className }) => <span className={`error text-danger ${className}`}></span>;

const COUNTRY_DROPDOWN_HEIGHT_MOBILE = 670;
const COUNTRY_DROPDOWN_HEIGHT = 224;

const BookACallForm: FC<BookACallFormProps> = ({
  styles = defaultStyles,
  containerRef,
  onBookACallSubmit,
} = emptyObj) => {
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [formErrors, setFormErrors] = useState(DEFAULT_OBJ);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [errorFieldsRef] = useState(() =>
    Object.entries(DEFAULT_FORM_VALUES).reduce((refs, [key]) => {
      refs[key] = createRef();
      return refs;
    }, {}),
  );
  const [isBookACallCtaLoading, setIsBookACallCtaLoading] = useState(false);
  const bookACallCtaText = useBookACallText({ defaultText: "BOOK A DEMO" });

  const { componentName = "" } = useJuneSoComponentContext();

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { country_code: countryIso = DEFAULT_COUNTRY_ISO } = getJsonCookie("geo") || DEFAULT_OBJ;
  const { loginWithRedirect } = useAuth0V2();
  const { query: { sourceOrigin = "" } = DEFAULT_OBJ } = useRouter();
  const { captchaElement, submitAsyncValidator } = useGoogleReCaptcha({
    sitekey: GOOGLE_RECAPTCHA_SITEKEY,
  });
  const { countryDropdownOptions, countriesMappingUsingIso } = useCountryMapping();
  const { [hubspotContextRefactorFlag]: hubspotContextRefactor } = useFlags();

  const [selectedCountry, setSelectedCountry] = useState(emptyObj);

  const isGdprCountry = useMemo(
    () => GDPR_COUNTRIES_ISO.includes(selectedCountry?.iso),
    [selectedCountry?.iso],
  );

  useEffect(() => {
    if (countriesMappingUsingIso[countryIso])
      setSelectedCountry(countriesMappingUsingIso[countryIso]);
  }, [countriesMappingUsingIso, countryIso]);

  const {
    email: { value: emailValue },
    role: { value: roleValue },
    stadiumUsage: { value: stadiumUsageValue },
    subscribeMarketingEmail: { value: subscribeMarketingEmailValue },
    companySize: { value: companySizeValue },
  } = formValues;

  const {
    fullName: fullNameError,
    email: emailError,
    companyName: companyNameError,
    role: roleError,
    stadiumUsage: stadiumUsageError,
    phone: phoneError,
    companySize: companySizeError,
  } = formErrors;

  useEffect(() => {
    initializeChiliPiper();
  }, []);

  useEffect(() => {
    const personalEmailWarning = isNotWorkEmail(emailValue);
    setShowEmailWarning(personalEmailWarning);
    setIsTouched(true);
  }, [emailValue]);

  const handleEmailAndPhoneBlur = () => {
    if (!formValues.email.value) return;

    const payload = [];

    Object.entries(formValues).forEach(([key, value]) => {
      if (isEmpty(value.value)) return;

      if (key === FORM_FIELDS_KEYS.fullName) {
        const { firstName, lastName } = getFirstAndLastName(value.value);
        payload.push(
          { name: "firstName", value: firstName },
          { name: "lastName", value: lastName },
        );
      } else if (Array.isArray(value.value)) {
        payload.push({ name: key, value: value.value.join(", ") });
      } else {
        payload.push({ name: key, value: value.value });
      }
    });

    if (selectedCountry?.name) {
      payload.push({ name: "country", value: selectedCountry.name });
    }
    postHubspotFormData({
      payload,
      pageName: getPageName(),
      hubspotContextRefactor: hubspotContextRefactor as boolean,
    });
  };

  const emailInputBlurHandler = () => {
    setIsTouched(false);
    handleEmailAndPhoneBlur();
  };

  const getFieldAttributes = (fieldName) => ({
    name: fieldName,
    id: fieldName,
    value: formValues[fieldName].value,
    error: !!formErrors[fieldName],
  });

  const handleFieldChange = ({ name, value, other = false, otherValue = "" }) => {
    const updatedFormErrors = omit(formErrors, name);
    const updatedFormValues = {
      ...formValues,
      [name]: {
        ...formValues[name],
        value,
        ...(OTHER_INPUT_FIELDS_KEY.includes(name) ? { other } : {}),
        ...(name === FORM_FIELDS_KEYS.offerings ? { otherValue } : {}),
      },
    };
    setFormValues((prevFormValues) => ({ ...prevFormValues, ...updatedFormValues }));
    setFormErrors(updatedFormErrors);
  };

  const handleSubscribeMarketingEmailChange = ({ checked }) => {
    handleFieldChange({
      name: FORM_FIELDS_KEYS.subscribeMarketingEmail,
      value: checked,
    });
  };

  const saveSubmission = async () => {
    const orderInfo = {
      cartInfo: "",
      shopNumber: "",
    };
    if (onBookACallSubmit) {
      const bookACallAdditionalInfo = await onBookACallSubmit?.();
      orderInfo.cartInfo = bookACallAdditionalInfo?.cartInfo || "";
      orderInfo.shopNumber = bookACallAdditionalInfo?.shopNumber || "";
    }
    const { cartInfo, shopNumber } = orderInfo;
    const mappedFormValues = mapValues(formValues, "value");
    const { offerings, fullName, phone, subscribeMarketingEmail } = mappedFormValues;
    const otherOfferings = formValues[FORM_FIELDS_KEYS.offerings]?.otherValue;
    const modifiedMappedFormValues = omit(
      mappedFormValues,
      FORM_FIELDS_KEYS.fullName,
      FORM_FIELDS_KEYS.phone,
      FORM_FIELDS_KEYS.offerings,
      FORM_FIELDS_KEYS.subscribeMarketingEmail,
    );
    const { firstName, lastName } = getFirstAndLastName(fullName); //Need to extract this way to keep the synced changes on Hubspot and Chilipiper
    const searchParams = new URLSearchParams(window.location.search);
    const interestedPackage = searchParams.get("interestedPackage") || "";
    const sourceUrl = window.location.href.split("//")?.[1] || ""; //WAF block full url

    const formattedOfferings = getFormattedOfferings({
      offeringsValue: offerings,
      offeringsOtherValue: otherOfferings,
    });

    const parsedFormValues = {
      ...modifiedMappedFormValues,
      firstName,
      lastName,
      offerings: [...offerings, otherOfferings].filter(Boolean).join(", "),
      country: selectedCountry?.name,
      treatType: STADIUM_BOOK_A_CALL_TREAT_TYPE,
      sourceOrigin: getDisplayedSourceOrigin(sourceOrigin),
      phone: `${selectedCountry?.dialCode} ${phone}`,
      sourceUrl,
      ...(isGdprCountry ? { subscribeMarketingEmail } : {}),
      interestedPackage: interestedPackage ? `Interested Package: ${interestedPackage}` : "",
      ...(cartInfo || shopNumber ? { cartInfo, shopNumber } : {}),
    };
    const formattedResponse = getFormattedResponse({
      ...parsedFormValues,
    });

    const data = await bookACallApi(formattedResponse).catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    });

    const formattedResponseForAuthParams = getFormattedResponseForAuthParams({
      ...parsedFormValues,
      ...data,
    });
    return {
      formattedResponse: { ...formattedResponse, ...formattedOfferings },
      formattedResponseForAuthParams,
    };
  };

  const getChiliPiperAppName = () => {
    const isSwagDomain = window.location.origin === new URL(SWAGMAGIC_URL).origin;
    return isSwagDomain ? SWAGMAGIC : APP_NAME;
  };

  const submitForm = (formattedResponse, formattedResponseForAuthParams) => {
    let isSuccess = false;
    const formattedResponseForAnalytics = getFormattedResponseForAnalytics(formattedResponse);

    gtagEvent(BOOK_A_CALL_FORM_SUBMITTED, formattedResponseForAnalytics);

    submitFormData(
      HUBSPOT_BOOK_A_CALL_FORM_GUID,
      parseParamsForHubspot(
        BOOK_A_CALL_HUBSPOT_FORM_MAPPING,
        { ...formattedResponseForAnalytics },
        hubspotContextRefactor ? getPageName() : BOOK_A_CALL_PAGE_NAME,
        hubspotContextRefactor as boolean,
      ),
    );

    const redirectToResourcesPage = () =>
      loginWithRedirect({
        redirectUri: window.location.origin + BASE_PATH + LOGIN_CALLBACK_ROUTE,
        appState: {
          returnTo: THANK_YOU_PAGE_URL,
        },
        ...formattedResponseForAuthParams,
      });

    submitChiliPiper(
      {
        ...formattedResponseForAnalytics,
      },
      getChiliPiperRouter(getChiliPiperAppName()),
      {
        onSuccess: () => {
          gtagEvent(CALL_BOOKED);
          isSuccess = true;
          isGmailAccount(emailValue)
            ? setTimeout(redirectToResourcesPage, 10000) //Sufficient 10 secs added for gmail users because of 'unknown sender' google policy leading to scheduled calls being missed.
            : redirectToResourcesPage();
        },
        onClose: () => {
          if (isSuccess) redirectToResourcesPage();
        },
      },
    );
    setFormValues({ ...DEFAULT_FORM_VALUES });
    setFormErrors(DEFAULT_OBJ);
  };

  const submitBookACallForm = async (withSubmit = true) => {
    try {
      if (withSubmit) {
        const isValidated = !isAutomationEmail(emailValue) ? await submitAsyncValidator() : true;
        if (isValidated) {
          const { formattedResponse, formattedResponseForAuthParams } = await saveSubmission();
          submitForm(formattedResponse, formattedResponseForAuthParams);
        }
      } else {
        await saveSubmission();
        scrollToTop();
      }
    } finally {
      setIsBookACallCtaLoading(false);
    }
  };

  const handleValidate = async (event, withSubmit = true) => {
    setIsBookACallCtaLoading(true);
    event.preventDefault();
    const errors = {};

    const fields = Object.keys(formValues);

    for (const field of fields) {
      const fieldObj = formValues[field];
      if (field === FORM_FIELDS_KEYS.offerings) {
        const { value = "", otherValue = "", validates = {} } = fieldObj;
        if (validates.presence && isEmpty(value) && isEmpty(otherValue)) {
          errors[field] = "Required";
        }
        continue;
      }
      const err = Validator(fieldObj);
      if (err.length) {
        errors[field] = err[0];
      }
    }

    if (Object.keys(errors).length) {
      setIsBookACallCtaLoading(false);
      setFormErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      const firstErrElementRef = errorFieldsRef[firstErrorField];
      const offset = isMobileDevice
        ? firstErrElementRef?.current?.clientHeight + 60
        : firstErrElementRef?.current?.clientHeight;
      return scrollToContainerWithRef({
        ref: firstErrElementRef,
        topOffset: offset,
        behavior: "instant",
      });
    }

    submitBookACallForm(withSubmit);
  };
  const showWarning = showEmailWarning && isTouched;

  const renderFullName = () => (
    <div className={styles.bookCallFormFieldHolder} ref={errorFieldsRef.fullName}>
      <TextInput
        {...getFieldAttributes(FORM_FIELDS_KEYS.fullName)}
        type="text"
        className={styles.bookCallFormInput}
        placeholder="Jennifer Olsen"
        validate={(fieldObj) => ({
          name: fieldObj.name,
          value: fieldObj.value,
        })}
        label={
          <Label showIcon={false} className={styles.inputLabel}>
            <div className={styles.bookCallFieldsLabelHolder}>
              FULL NAME
              <div className={styles.bookCallFormErrorHolder}>
                {!!fullNameError && <ErrorMsg msg={fullNameError} />}
              </div>
            </div>
          </Label>
        }
        onChange={handleFieldChange}
      />
    </div>
  );

  const renderCompanyName = () => (
    <div className={styles.bookCallFormFieldHolder} ref={errorFieldsRef.companyName}>
      <TextInput
        {...getFieldAttributes(FORM_FIELDS_KEYS.companyName)}
        type="text"
        className={styles.bookCallFormInput}
        placeholder="Company name"
        validate={(fieldObj) => ({
          name: fieldObj.name,
          value: fieldObj.value,
        })}
        label={
          <Label showIcon={false} className={styles.inputLabel}>
            <div className={styles.bookCallFieldsLabelHolder}>
              Company
              <div className={styles.bookCallFormErrorHolder}>
                {!!companyNameError && <ErrorMsg msg={companyNameError} />}
              </div>
            </div>
          </Label>
        }
        onChange={handleFieldChange}
      />
    </div>
  );

  const renderEmail = () => (
    <div className={styles.bookCallFormFieldHolder} ref={errorFieldsRef.email}>
      <TextInput
        {...getFieldAttributes(FORM_FIELDS_KEYS.email)}
        type="email"
        className={styles.bookCallFormInput}
        prefix={
          showWarning && (
            <div className={styles.emailTippyCont}>
              <ToolTip
                interactive
                placement="bottom-end"
                customClassName={styles.bookCallTippyContainer}
                content={
                  <div className="">
                    <div className={styles.tippyHead}>{EMAIL_WARNING.heading}</div>
                    <div className={styles.tippyDescription}>{EMAIL_WARNING.description}</div>
                    <Button
                      styleType="no-style"
                      onClick={() => setShowEmailWarning(false)}
                      className={styles.closeIcon}
                    />
                  </div>
                }
                visible={showWarning}
                trigger="click"
              >
                <span className={styles.iconWarning}></span>
              </ToolTip>
            </div>
          )
        }
        placeholder="you@company.com"
        validate={(fieldObj) => ({
          name: fieldObj.name,
          value: fieldObj.value,
        })}
        label={
          <Label showIcon={false} className={styles.inputLabel}>
            <div className={styles.bookCallFieldsLabelHolder}>
              WORK EMAIL
              <div className={styles.bookCallFormErrorHolder}>
                {!!emailError && <ErrorMsg msg={emailError} />}
              </div>
            </div>
          </Label>
        }
        onChange={handleFieldChange}
        onBlur={emailInputBlurHandler}
      />
    </div>
  );

  const handleCountrySelect = ({ value }) => {
    setSelectedCountry(countriesMappingUsingIso[value]);
  };

  const renderCountryDropdownOptions = ({ dropdownOptions, renderOption }) => {
    const height = isMobileDevice ? COUNTRY_DROPDOWN_HEIGHT_MOBILE : COUNTRY_DROPDOWN_HEIGHT;
    return (
      <ReactWindowList
        height={height}
        itemCount={dropdownOptions.length}
        itemData={dropdownOptions}
        itemSize={42}
        width="100%"
        renderOption={renderOption}
      />
    );
  };

  const renderCountryAndPhoneNumber = () => (
    <div className={styles.bookCallFormFieldHolder}>
      <div className={styles.label}>PHONE NUMBER</div>
      <div className={styles.bookCallPhoneNumberRow}>
        <div className={styles.bookCallCountryNameBlock}>
          <div className={styles.bookCallCountryFlagImageBlock} aria-label="country flag">
            <SelectDropdown
              options={countryDropdownOptions}
              renderOptions={renderCountryDropdownOptions}
              showSearchFilter
              searchfilterOptions={{
                placeholder: "Search",
                attributes: ["iso", "name"],
              }}
              inputClassName={styles.countrySearchTextInput}
              onSelect={handleCountrySelect}
              value={selectedCountry}
              text={
                selectedCountry?.id ? (
                  <CountryFlag
                    key={selectedCountry?.id}
                    country={selectedCountry}
                    className={styles.imgFlag}
                    type="round"
                  />
                ) : (
                  <Spinner />
                )
              }
              classes={styles.programDropdown}
              menuClass={styles.countryDropdownMenu}
              menuClassName={styles.countryFlagDropdownSection}
              responsive
              applyDefaultItemClass={false}
            />
          </div>
        </div>
        <div className={styles.bookCallPhoneInputBlock}>
          <div
            className={classNames(
              styles.bookCallPhoneCodeBlock,
              styles.bookCallPhoneCodeBlockWhiteBg,
            )}
          >
            <input
              readOnly
              type="text"
              className={styles.globalInput}
              placeholder={selectedCountry?.dialCode}
            />
          </div>
          <div
            className={`${styles.bookCallPhoneInputHolder}
            ${styles.bookCallPhoneInputHolderV2}`}
          >
            <TextInput
              {...getFieldAttributes(FORM_FIELDS_KEYS.phone)}
              type="text"
              className={styles.bookCallFormInput}
              placeholder="xxx xxx xxxx"
              validate={(fieldObj) => ({
                name: fieldObj.name,
                value: normalizePhone(fieldObj.value),
              })}
              label={{
                for: FORM_FIELDS_KEYS.phone,
                content: "Phone Number",
                classes: styles.quoteContentHead,
              }}
              onChange={handleFieldChange}
              onBlur={handleEmailAndPhoneBlur}
              aria-label="Phone Number"
            />
          </div>
        </div>
      </div>
      {!!phoneError && <ErrorMsg msg={phoneError} />}
    </div>
  );

  const renderHowCanWeHelp = () => (
    <div className={styles.bookCallFormFieldHolderFull}>
      <TextAreaInput
        {...getFieldAttributes(FORM_FIELDS_KEYS.additionalinfo)}
        className={styles.bookCallTextArea}
        placeholder="What would your ideal solution look like? Anything else we should know?"
        label={
          <Label className={styles.label} showIcon={false}>
            <div className={styles.bookCallAdditionalInfoRow}>how can we help?</div>
          </Label>
        }
        onChange={handleFieldChange}
      />
    </div>
  );

  const renderSubscribeMarketingEmailCheckbox = () => (
    <CheckboxInput
      {...getFieldAttributes(FORM_FIELDS_KEYS.subscribeMarketingEmail)}
      value="I’d like to stay in the loop about Stadium via email."
      onChange={handleSubscribeMarketingEmailChange}
      checked={subscribeMarketingEmailValue}
      id={FORM_FIELDS_KEYS.subscribeMarketingEmail}
      label={
        <Label
          styles={styles}
          iconType="check"
          className={styles.bookCallCustomizeYourDemoDropdownCheckboxHolder}
        >
          <span>I’d like to stay in the loop about Stadium via email.</span>
        </Label>
      }
    />
  );

  const renderNewCompanySizes = () => (
    <SingleSelectDropdown
      fieldLabel="COMPANY SIZE"
      formValues={formValues}
      inputData={COMPANY_INPUT}
      fieldValue={companySizeValue}
      fieldError={companySizeError}
      errorFieldsRef={errorFieldsRef}
      handleFieldChange={handleFieldChange}
      styles={styles}
    />
  );

  const bookACallFormId = `${componentName}${componentName ? "-" : ""}book-a-call-form`;

  return (
    <section className={classNames(styles.bookCallFormSection)}>
      <div className={styles.bookCallFormContentHolder} ref={containerRef}>
        <form onSubmit={handleValidate} name={bookACallFormId} id={bookACallFormId}>
          <div className={styles.bookCallFormHolder}>
            {renderFullName()}
            {renderEmail()}
            {renderCountryAndPhoneNumber()}
            {renderCompanyName()}
            <SingleSelectDropdown
              fieldLabel="TEAM"
              formValues={formValues}
              inputData={TEAM_INPUT}
              fieldValue={roleValue}
              fieldError={roleError}
              handleFieldChange={handleFieldChange}
              errorFieldsRef={errorFieldsRef}
              styles={styles}
            />

            {renderNewCompanySizes()}
            <CustomizeDemoDropdown
              formValues={formValues}
              errorFieldsRef={errorFieldsRef}
              formErrors={formErrors}
              handleFieldChange={handleFieldChange}
              styles={styles}
            />
            <SingleSelectDropdown
              fieldLabel="How often would you like to use Stadium?"
              formValues={formValues}
              inputData={STADIUM_USAGE}
              fieldValue={stadiumUsageValue}
              fieldError={stadiumUsageError}
              handleFieldChange={handleFieldChange}
              errorFieldsRef={errorFieldsRef}
              renderTextArea={true}
              styles={styles}
            />
            {renderHowCanWeHelp()}
            {isGdprCountry && renderSubscribeMarketingEmailCheckbox()}
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              styleType="primary"
              className={styles.bookCallFormButton}
              title={bookACallCtaText}
              isLoading={isBookACallCtaLoading}
              disableOnLoad={true}
              juneSoProps={{ componentName, target: BOOK_A_CALL, services: BOOK_A_CALL_SERVICES }}
            >
              {bookACallCtaText}
            </Button>
          </div>
          {!isAutomationEmail(emailValue) && captchaElement}
        </form>
      </div>
    </section>
  );
};

export default withSyncFeatureFlag(BookACallForm, {
  loader: <PageLoader wrapperClass={defaultStyles.loaderWrapper} />,
});
